<template>
  <div class="submission-form">
    <h1 class="title has-text-primary" @click="_felixIsLazy">Create New Project</h1>
    <b-field custom-class="has-text-primary is-size-5" label="Client Name">
      <b-input v-model="clientName" placeholder="e.g. John and Jane Doe"></b-input>
    </b-field>
    <b-field custom-class="has-text-primary is-size-5" label="Project Address">
      <b-input v-model="projectAddress" placeholder="e.g. 32 Kempson Way"></b-input>
    </b-field>
    <b-field custom-class="has-text-primary is-size-5" label="Project Postcode">
      <b-input v-model="projectPostcode" placeholder="e.g. IP32 7AR"></b-input>
    </b-field>
    <b-field custom-class="has-text-primary is-size-5" label="Project start date (if known)">
      <b-datepicker
          v-model="startDate"
          :first-day-of-week="firstDayOfWeek"
          :icon-right="startDate ? 'close-circle' : ''"
          :locale="locale"
          icon="calendar"
          icon-right-clickable
          placeholder="Click to select..."
          position="is-top-left"
          rounded
          @icon-right-click="clearDateTime">
      </b-datepicker>
    </b-field>
    <section class="service-container">
      <b-field custom-class="has-text-primary is-size-5"
               group-multiline
               grouped
               label="Service"
               position="is-centered"
      >
        <b-field>
          <b-tooltip :active="tokensLeft.length === 0"
                     type="is-danger"
                     label="You have no tokens left, please contact the office"
          >
            <b-radio v-model="selectedService"
                     native-value="PCE"
                     type="is-primary"
                     :disabled="tokensLeft.length === 0"
            >
              <span>Primary Cost Estimate</span>
            </b-radio>
          </b-tooltip>
        </b-field>
        <b-field>
          <b-radio v-model="selectedService"
                   native-value="Estimating Service"
                   type="is-primary"
          >
            <span>Estimating Service</span>
          </b-radio>
        </b-field>
        <b-field>
          <b-radio v-model="selectedService"
                   v-tooltip="'Coming Soon'"
                   disabled
                   native-value="Self Estimating"
                   type="is-primary"
          >
            Self-Estimating
          </b-radio>
        </b-field>
      </b-field>
    </section>
    <b-field custom-class="has-text-primary is-size-5" label="Job Type"
             v-tooltip="!selectedService ? 'Please select a service' : ''">
      <p class="control">
        <b-dropdown
            :mobile-modal="true"
            :open-on-focus="true"
            aria-role="list"
            expanded
            size="is-medium"
            @input="option => (selectedJobType = option)"
            :disabled="!selectedService"
        >
          <template #trigger="{ active }">
            <b-input
                :icon-right="active ? 'caret-up' : 'caret-down'"
                :value="selectedJobType ? selectedJobType.label : 'Select Job Type'"
                expanded
                readonly/>
          </template>
          <b-dropdown-item
              v-for="option in jobTypes"
              :key="option.name"
              :value="option"
          >
            {{ option.label }}
          </b-dropdown-item>
        </b-dropdown>
      </p>
    </b-field>
    <upload-files :class="{expanded : selectedService === 'Estimating Service'}" class="upload-container"
                  ref="uploadFiles"/>
    <b-field label="Additional Notes" custom-class="has-text-primary is-size-5">
      <b-input v-model="additionalNotes" maxlength="600" type="textarea"></b-input>
    </b-field>
    <b-field v-if="selectedService" class="terms-and-conditions">
      <b-checkbox v-model="acceptedTerms">
        I confirm that I have read and understood the <a
          href="https://projectestimator.co.uk/terms-conditions-estimating-services/" target="_blank">Terms and
        Conditions</a>
      </b-checkbox>
    </b-field>
    <b-field v-if="selectedService === 'PCE'" class="terms-and-conditions">
      <b-checkbox v-model="acknowledgedTokenUse" type="is-danger">
        I acknowledge that a token will be used to purchase the requested service, leaving me with <span
          class="has-text-weight-bold">{{ tokensLeft.length - 1 }}</span> tokens.
      </b-checkbox>
    </b-field>
    <b-field v-if="selectedService" :label="selectedService === 'Estimating Service' ? 'Promo code' : ''"
             custom-class="has-text-primary is-size-5" grouped multiline expanded>
      <b-input v-if="selectedService === 'Estimating Service'" v-model="promoCode"></b-input>
      <p class="control mb-2 is-expanded">
        <b-button class="is-primary is-fullwidth has-text-white has-text-weight-semibold"
                  :disabled="!acceptedTerms || !selectedJobType || !selectedService || (!acknowledgedTokenUse && selectedService === 'PCE')"
                  icon-right="paper-plane"
                  type="button"
                  @click="submitProject">
          Submit
        </b-button>
      </p>
    </b-field>
  </div>
</template>

<script>
import UploadFiles from "component/upload-files";
import Page from "../../../secure/entries/co-pilot/routes/primary-cost-estimate/page.vue";
import QuoteForm from "../../../secure/resource/module/quoteForm";
import MapField from "../../module/mapField";

export default {
  name: "submission-form",
  components: {
    UploadFiles,
    Page,
  },
  props: {
    fetchPCE: {
      type: Boolean,
      default: true
    }
  },
  created() {
    if (this.fetchPCE) {
      this.$store.dispatch('pce/fetchAvailableJobs');
    }
  },
  data() {
    return {
      quoteForm: new QuoteForm('addProject'),
      selectedJobType: null,
      showWeekNumber: false,
      enableSeconds: false,
      hourFormat: undefined,
      locale: undefined,
      firstDayOfWeek: undefined,
      selectedService: '',
      acceptedTerms: false,
      additionalNotes: '',
      promoCode: '',
      acknowledgedTokenUse: false,
    };
  },
  watch: {
    'selectedJobType': {
      handler: function () {
        if (!this.selectedJobType) {
          return;
        }

        this.primaryCostEstimateJobType = this.selectedJobType.label;
        this.$store.commit('pce/setSelectedJobType', this.selectedJobType);
      }
    },
    'selectedService': {
      handler: function () {
        if (!this.selectedService) {
          return;
        }

        if (this.selectedService === 'Estimating Service') {
          this.quantityTakeOff = true;
          this.primaryCostEstimate = false;
        } else if (this.selectedService === 'PCE') {
          this.primaryCostEstimate = true;
          this.quantityTakeOff = false;
        }
      }
    }
  },
  computed: {
    jobTypes() {
      return this.$store.getters['pce/availableJobs'];
    },
    tokensLeft() {
      return this.$store.getters['company/availableTokensForType']('pce');
    },
    ...new MapField()
        .namespace('quoteForm-addProject-1')
        .add('clientName', 'form.quoteInfo.customer.name')
        .add('startDate', 'form.quoteInfo.customer.startDate')
        .add('projectAddress', 'form.quoteInfo.customer.address.line1')
        .add('projectPostcode', 'form.quoteInfo.customer.address.postcode')

        .add('quantityTakeOff', 'form.requestedServices.quantityTakeOff')
        .add('quantityTakeOffJobType', 'form.service.quantityTakeOff.jobType')
        .add('primaryCostEstimate', 'form.requestedServices.primaryCostEstimate')
        .add('primaryCostEstimateJobType', 'form.service.primaryCostEstimate.jobType')
        .add('note', 'note')

        .get()
  },
  methods: {
    _felixIsLazy(event) {
      if (event && event.shiftKey === true) {
        this.clientName = 'Dev Test';
        this.startDate = new Date('2024-07-01');
        this.projectAddress = '666 Harrier Way';
        this.projectPostcode = 'IP66 6FB';

        this.additionalNotes = 'ALALALALALALAL';
      }
    },
    clearDateTime() {
      this.startDate = null;
    },
    setModifiers() {
      if (!this.jobConfig && !this.jobConfig.workSections) {
        return {};
      }

      this.workSectionsConfigWithModifiers = [];
      this.selectedModifiers = {};

      _.forEach(this.jobConfig.workSections, (workSection) => {
        if (workSection.modifiers.length > 0) {
          this.workSectionsConfigWithModifiers.push(workSection);
        }
      });
    },
    submitProject() {
      const plansToUse = this.$refs.uploadFiles.files;
      this.$refs.uploadFiles.clearFiles();

      this.$store.dispatch('company/addQuote', {
        company: this.$store.getters['company/selectedCompany'],
        note: this.additionalNotes,
        sourceCampaign: this.promoCode,
        form: this.$store.getters['quoteForm-addProject-1/getPartialForm'](['quoteInfo', 'service', 'requestedServices', 'requestedQuotes']),
        plans: plansToUse
      }).then(() => {
        if (this.selectedService === 'PCE') {
          this.$router.push('/primary-cost-estimate');
        } else if (this.selectedService === 'Estimating Service') {
        }
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.submission-form {
  width: 50%;
  margin: auto;
  padding: 20px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  background-color: white;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);

  @include tablet--large {
    width: 80%;
  }

  .service-container {
    margin-bottom: 1.25em;
  }

  .upload-container {
    overflow: hidden;
    max-height: 0;
    transition: max-height 0.5s ease-in-out;

    &.expanded {
      max-height: 700px;
    }
  }

  .terms-and-conditions {
    font-size: 0.9rem;
    display: block;
  }
}
</style>